import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '../components/shared/Layout';
import { getReports } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { getScoreLabel } from '../utils/parScore';
import './DashboardPage.css';

export default function DashboardPage() {
  const { agent, refreshAgent } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const creditSuccess = searchParams.get('credits') === 'success';

  useEffect(() => {
    if (creditSuccess) refreshAgent();
    getReports()
      .then(res => setReports(res.data))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <Layout>
      {creditSuccess && (
        <div className="alert alert-success" style={{marginBottom: 24}}>
          🎉 Credits added successfully! Your balance has been updated.
        </div>
      )}

      {/* Header row */}
      <div className="dash-header">
        <div>
          <h1 className="dash-title">Welcome back, {agent?.full_name?.split(' ')[0]}</h1>
          <p className="dash-subtitle">Generate and manage your PAR reports</p>
        </div>
        <Link to="/reports/new" className="btn btn-primary btn-lg">
          + New Report
        </Link>
      </div>

      {/* Stat cards */}
      <div className="stat-grid">
        <div className="stat-card">
          <div className="stat-value" style={{color: agent?.credits > 0 ? 'var(--navy)' : 'var(--red)'}}>
            {agent?.credits ?? 0}
          </div>
          <div className="stat-label">Credits Remaining</div>
          {agent?.credits === 0 && (
            <Link to="/credits" className="btn btn-gold btn-sm" style={{marginTop: 10}}>Buy Credits</Link>
          )}
        </div>
        <div className="stat-card">
          <div className="stat-value">{reports.length}</div>
          <div className="stat-label">Reports Generated</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {reports.filter(r => r.status === 'generated').length}
          </div>
          <div className="stat-label">Completed Reports</div>
        </div>
      </div>

      {/* No credits CTA */}
      {agent?.credits === 0 && (
        <div className="no-credits-banner">
          <div>
            <strong>You're out of credits.</strong> Purchase a pack to generate your next PAR report.
          </div>
          <Link to="/credits" className="btn btn-gold">View Credit Packs</Link>
        </div>
      )}

      {/* Reports table */}
      <div className="card" style={{marginTop: 28}}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 20}}>
          <h2 className="card-title">Recent Reports</h2>
          {reports.length > 0 && (
            <Link to="/reports/new" className="btn btn-outline btn-sm">+ New Report</Link>
          )}
        </div>

        {loading ? (
          <div style={{textAlign:'center', padding: 40}}><div className="spinner spinner-dark" style={{margin:'0 auto'}}/></div>
        ) : reports.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <p>No reports yet. Generate your first PAR report to get started.</p>
            <Link to="/reports/new" className="btn btn-primary" style={{marginTop: 16}}>Generate First Report</Link>
          </div>
        ) : (
          <table className="reports-table">
            <thead>
              <tr>
                <th>Property Address</th>
                <th>PAR Score</th>
                <th>Market</th>
                <th>Date</th>
                <th>PDF</th>
              </tr>
            </thead>
            <tbody>
              {reports.map(r => {
                const info = r.par_score ? getScoreLabel(r.par_score) : null;
                return (
                  <tr key={r.id}>
                    <td className="addr-cell">{r.property_address}</td>
                    <td>
                      {info ? (
                        <span className="score-badge" style={{background: info.color, width:42, height:42, fontSize:14}}>
                          {r.par_score}
                        </span>
                      ) : '—'}
                    </td>
                    <td>{info ? <span style={{color: info.color, fontSize:13, fontWeight:600}}>{info.label}</span> : '—'}</td>
                    <td style={{color:'var(--gray-400)', fontSize:13}}>{formatDate(r.created_at)}</td>
                    <td>
                      {r.pdf_url
                        ? <a href={r.pdf_url} target="_blank" rel="noreferrer" className="btn btn-outline btn-sm">Download</a>
                        : <span style={{color:'var(--gray-400)', fontSize:13}}>—</span>
                      }
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </Layout>
  );
}
